/**
 * ProcessDAG — React Flow canvas showing the model as a directed acyclic graph.
 *
 * Runs ELK layout on model change, renders ModelingNode and ModelingEdge components.
 * Layer 1 of the DAG view.
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactFlow, {
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useViewport,
} from 'reactflow';
import type { Node, NodeChange, ReactFlowInstance } from 'reactflow';
import 'reactflow/dist/style.css';

import ModelingNodeComponent, { MODELING_NODE_HEIGHT, MODELING_NODE_WIDTH } from './ModelingNode';
import type { ModelingNodeData } from './ModelingNode';
import ModelingEdgeComponent from './ModelingEdge';
import type { ModelingEdgeData } from './ModelingEdge';
import { layoutProductionTiers, COLUMN_PAD } from './processTierLayout';
import type { TierLaneData, ColumnBandData, TypeColumnMeta, TierBandMeta } from './processTierLayout';
import type { ModelDefinition, ModelRun, RunStatus, ModelingNodeType } from '../../types/modeling';
import { getNodeTypeColor, getNodeTypeColorResolved, getNodeTypeLabel, resolveCssVar } from '../../types/modeling';
import type { Cluster } from '../../types/cluster';
import { CLUSTER_VOCAB } from '../../types/cluster';
import ClusterRegionNode from '../cluster/ClusterRegionNode';
import StepNumberNode from '../cluster/StepNumberNode';
import { buildClusteredElkGraph, GRAPH_LAYER, UNSORTED_CLUSTER_NODE_ID } from '../cluster/clusterLayout';
import GraphCanvas from '../cluster/GraphCanvas';
import type { CanvasLegendEntry } from '../cluster/GraphCanvas';
import { HighlightContext, EMPTY_HIGHLIGHT } from '../shared/HighlightContext';
import type { HighlightState } from '../shared/HighlightContext';
import { decodeEntities } from '../../utils/planGraphData';
import { SelectedClusterContext } from '../shared/SelectedClusterContext';
import { clusterHighlight } from '../shared/clusterHighlight';
import type { HighlightEdge } from '../shared/clusterHighlight';

const CATEGORY_LEGEND_ORDER: ModelingNodeType[] = [
  'input_data', 'parameter', 'transform', 'calculate', 'validate', 'output',
];

// Frozen-axis chrome (screen coordinates) — mirrors PlanGraphView.
const HEADER_H = 32;
const GUTTER_W = 132;

// Zoom bounds mirror the plan graph's locked workspace.
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.25;

/** Neutral zebra band behind one dependency tier (plan-graph step-lane vocab).
 *  The tier LABEL is rendered by the frozen gutter, not here — this is a pure
 *  OPAQUE row band painted under the cards/edges (mirrors LaneBandNode). */
function TierLaneNode({ data }: { data: TierLaneData }) {
  return (
    <div
      aria-hidden
      style={{
        width: data.width, height: data.height, boxSizing: 'border-box',
        borderTop: '1px solid var(--border)',
        background: data.shaded ? 'var(--bg-warm)' : 'var(--surface)',
        pointerEvents: 'none',
      }}
    />
  );
}

/** Type-column band — full-height vertical hairline divider only (mirrors
 *  PlanGraphView.ColumnBandNode). Named 'columnBand' to match the ProcessDAG
 *  click/double-click guards. */
function ColumnBandNode({ data }: { data: ColumnBandData }) {
  return (
    <div
      aria-hidden
      style={{
        width: data.width, height: data.height, boxSizing: 'border-box',
        borderLeft: '1px solid var(--border)', background: 'transparent', pointerEvents: 'none',
      }}
    />
  );
}

const nodeTypes = {
  modelingNode: ModelingNodeComponent,
  tierLane: TierLaneNode,
  columnBand: ColumnBandNode,
  'cluster-region': ClusterRegionNode,
  // Build view splits the cluster box: the header rides above the edges as a
  // separate child node, reusing the same component (data.layer === 'header').
  'cluster-region-header': ClusterRegionNode,
  'step-number': StepNumberNode,
};
const edgeTypes = { modelingEdge: ModelingEdgeComponent };

// =============================================================================
// FROZEN AXES (screen-space HTML overlays, viewport-synced) — mirror
// PlanGraphView's PhaseHeader / StepGutter / CornerBox.
// =============================================================================

/** Frozen column header — one cell per present type column, type label in the
 *  type colour (mirrors PlanGraphView.PhaseHeader). */
function TypeColumnHeader({ columns }: { columns: TypeColumnMeta[] }) {
  const vp = useViewport();
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H,
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}
    >
      {columns.map((col) => (
        <div
          key={col.type}
          style={{
            position: 'absolute', top: 0, height: HEADER_H,
            left: col.left * vp.zoom + vp.x, width: col.width * vp.zoom,
            borderLeft: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: col.color, whiteSpace: 'nowrap' }}>
            {col.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Frozen left gutter — one label cell per dependency tier, carrying the same
 *  zebra shading as its grid row (mirrors PlanGraphView.StepGutter). */
function TierGutter({ tiers }: { tiers: TierBandMeta[] }) {
  const vp = useViewport();
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: GUTTER_W,
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
        background: 'var(--surface)', borderRight: '1px solid var(--border)',
      }}
    >
      {tiers.map((band) => (
        <div
          key={band.key}
          style={{
            position: 'absolute', left: 0, width: GUTTER_W,
            top: band.top * vp.zoom + vp.y, height: band.height * vp.zoom,
            borderTop: '1px solid var(--border)',
            background: band.shaded ? 'var(--bg-warm)' : 'var(--surface)',
            padding: '8px 12px', boxSizing: 'border-box', overflow: 'hidden',
          }}
        >
          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fg-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {band.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Frozen top-left corner box where the header + gutter meet. */
function CornerBox() {
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, width: GUTTER_W, height: HEADER_H,
        background: 'var(--surface)', borderRight: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)', zIndex: 6, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', paddingLeft: 12,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-secondary)' }}>
        Build
      </span>
    </div>
  );
}

/** Viewport-synced edge-label overlay (mirrors PlanGraphView.EdgeLabels): the
 *  active node's incident-edge artifact labels, lit on hover/select. Drawn above
 *  the canvas (pointer-events:none) so they never hide behind a node and always
 *  surface in the locked grid — where the per-edge labels did not (owner #3). */
function ProductionEdgeLabels({ labels }: { labels: { id: string; x: number; y: number; text: string }[] }) {
  const vp = useViewport();
  if (!labels.length) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12, overflow: 'hidden' }}>
      {labels.map((l) => (
        <div
          key={l.id}
          className="font-mono"
          style={{
            position: 'absolute',
            left: l.x * vp.zoom + vp.x,
            top: l.y * vp.zoom + vp.y,
            transform: 'translate(-50%, -50%)',
            background: 'var(--surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 6,
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--fg-secondary)',
            whiteSpace: 'nowrap',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          {l.text}
        </div>
      ))}
    </div>
  );
}

/**
 * Override cluster-region positions with saved (manually-dragged) ones. ELK
 * always recomputes a fresh region layout; a saved absolute position wins so
 * clusters stay where the user dropped them. Members + step numerals are
 * parent-relative children, so moving the region carries the whole cluster —
 * we only persist/restore the region itself.
 */
export function applySavedRegionPositions(
  rfNodes: Node[],
  saved: Record<string, { x: number; y: number }> | undefined,
): Node[] {
  if (!saved || Object.keys(saved).length === 0) return rfNodes;
  return rfNodes.map((node) => {
    const pos = node.type === 'cluster-region' ? saved[node.id] : undefined;
    return pos ? { ...node, position: pos } : node;
  });
}

interface ProcessDAGProps {
  model: ModelDefinition;
  clusters: Record<string, Cluster>;
  lastRun: ModelRun | null;
  activeRunProgress: ModelRun | null;
  onNodeClick: (nodeId: string) => void;
  onNodeDoubleClick: (nodeId: string) => void;
  onPaneClick: () => void;
  selectedNodeId: string | null;
  /**
   * Fired when the selected cluster changes (null when cleared). The parent
   * uses it to clear node selection so the NodeInfoBar closes — cluster and
   * node selection are mutually exclusive. Optional so embedding surfaces
   * (e.g. the build preview sandbox) don't have to wire it.
   */
  onClusterSelect?: (clusterId: string | null) => void;
  /**
   * Saved cluster-region positions (keyed by region id) from a prior drag.
   * When present they override the fresh ELK layout so manually-placed
   * clusters persist across reloads. Optional — sandboxes can omit it.
   */
  initialPositions?: Record<string, { x: number; y: number }>;
  /**
   * Persist cluster-region positions when a drag ends. Optional — when absent
   * (e.g. the build preview sandbox) dragging is purely ephemeral.
   */
  onPositionsChange?: (positions: Record<string, { x: number; y: number }>) => void;
  /** Gate drag-persistence; defaults true. */
  editable?: boolean;
  /** Controls rendered in the canvas header strip (the build run/history/
   *  done controls). The build workspace name lives in the workspace brief
   *  above, so the canvas header carries the legend + these controls, not a
   *  duplicated title. */
  headerActions?: React.ReactNode;
}

export default function ProcessDAG({
  model,
  clusters,
  lastRun,
  activeRunProgress,
  onNodeClick,
  onNodeDoubleClick,
  onPaneClick,
  selectedNodeId,
  onClusterSelect,
  initialPositions,
  onPositionsChange,
  editable = true,
  headerActions,
}: ProcessDAGProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [layoutDone, setLayoutDone] = useState(false);
  const [showEdgeLabels, setShowEdgeLabels] = useState(false);
  // Locked-grid metadata for the frozen header + gutter (mirrors PlanGraphView).
  const [gridColumns, setGridColumns] = useState<TypeColumnMeta[]>([]);
  const [gridTiers, setGridTiers] = useState<TierBandMeta[]>([]);
  const [gridWidth, setGridWidth] = useState(0);
  const [gridHeight, setGridHeight] = useState(0);
  // Pin/lock state — the viewport is pinned top-left ONCE, then never re-fit.
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  const [hasPinned, setHasPinned] = useState(false);
  // Bumped by the "Reset layout" button to force a fresh ELK pass that ignores
  // saved positions (a true reset to the clean auto-layout).
  const [reLayoutVersion, setReLayoutVersion] = useState(0);
  // Clicking a cluster region highlights its members + everything they connect
  // to. Mutually exclusive with node selection.
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  // Hovered node — drives edge-label reveal on hover, mirroring the plan graph
  // (PlanGraphView: activeId = hoveredId || selectedId). Hover wins over select.
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  // Build run status lookup from active progress or last completed run
  const runStatusMap = useMemo(() => {
    const map: Record<string, RunStatus> = {};
    const run = activeRunProgress || lastRun;
    if (run?.node_results) {
      run.node_results.forEach((nr) => {
        map[nr.node_id] = nr.status;
      });
    }
    // While a run is IN PROGRESS, seed every not-yet-finished node so the grid
    // shows the full pending → running → done progression. The backend only
    // appends a NodeResult once a node FINISHES (model_runner._persist_node_results),
    // so unstarted nodes have no entry — without seeding the user just sees dots
    // appear one-by-one with no sense of the queue. A node whose upstream deps are
    // all completed but which has no result yet is the live "frontier" → running;
    // anything still waiting on an upstream is pending.
    if (activeRunProgress) {
      const upstream = new Map<string, string[]>();
      model.nodes.forEach((n) => upstream.set(n.node_id, []));
      model.connections.forEach((c) => {
        const arr = upstream.get(c.to_node_id);
        if (arr) arr.push(c.from_node_id);
      });
      model.nodes.forEach((n) => {
        if (map[n.node_id]) return; // already has a real result
        const deps = upstream.get(n.node_id) || [];
        const ready = deps.every((d) => map[d] === 'completed');
        map[n.node_id] = ready ? 'running' : 'pending';
      });
    }
    return map;
  }, [activeRunProgress, lastRun, model]);

  // Live run-progress summary for the header strip (only while a run is active).
  const runProgress = useMemo(() => {
    if (!activeRunProgress) return null;
    const results = activeRunProgress.node_results || [];
    const total = model.nodes.length;
    const done = results.filter((r) => r.status === 'completed').length;
    const failed = results.some((r) => r.status === 'failed');
    const runningNode = model.nodes.find((n) => runStatusMap[n.node_id] === 'running');
    const runningName = runningNode ? decodeEntities(runningNode.name || '') : null;
    return { total, done, failed, runningName };
  }, [activeRunProgress, model, runStatusMap]);

  // Build validation summary lookup
  const validationMap = useMemo(() => {
    const map: Record<string, { pass: number; fail: number }> = {};
    const run = activeRunProgress || lastRun;
    if (run?.validation_results) {
      run.validation_results.forEach((vr) => {
        // Group by source node — validation_results have source_ids
        vr.source_ids.forEach((nodeId) => {
          if (!map[nodeId]) map[nodeId] = { pass: 0, fail: 0 };
          if (vr.passed) map[nodeId].pass++;
          else map[nodeId].fail++;
        });
      });
    }
    return map;
  }, [activeRunProgress, lastRun]);

  const runStatusMapRef = useRef(runStatusMap);
  const validationMapRef = useRef(validationMap);
  const selectedNodeIdRef = useRef(selectedNodeId);

  useEffect(() => {
    runStatusMapRef.current = runStatusMap;
  }, [runStatusMap]);

  useEffect(() => {
    validationMapRef.current = validationMap;
  }, [validationMap]);

  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  // Latest saved positions, read inside the async layout effect without making
  // it a dependency (it must not re-run ELK when only positions change).
  const initialPositionsRef = useRef(initialPositions);
  useEffect(() => {
    initialPositionsRef.current = initialPositions;
  }, [initialPositions]);

  const clustersForThisModel = useMemo(
    () => Object.values(clusters).filter((cluster) => cluster.model_id === model.model_id),
    [clusters, model.model_id],
  );

  // Dominant node category per cluster → drives the cluster column's
  // accent color (top bar + header wash). Ties broken by CATEGORY_LEGEND_ORDER.
  const accentByRegionId = useMemo<Record<string, string>>(() => {
    const nodeById = new Map(model.nodes.map((n) => [n.node_id, n]));
    const accentByCluster: Record<string, string> = {};
    clustersForThisModel.forEach((cluster) => {
      const counts: Partial<Record<ModelingNodeType, number>> = {};
      cluster.member_node_ids.forEach((nodeId) => {
        const nd = nodeById.get(nodeId);
        if (!nd) return;
        counts[nd.node_type] = (counts[nd.node_type] ?? 0) + 1;
      });
      let best: ModelingNodeType | null = null;
      let bestCount = -1;
      CATEGORY_LEGEND_ORDER.forEach((type) => {
        const c = counts[type] ?? 0;
        if (c > bestCount) {
          best = type;
          bestCount = c;
        }
      });
      if (best && bestCount > 0) {
        accentByCluster[cluster.cluster_id] = getNodeTypeColor(best);
      }
    });
    return accentByCluster;
  }, [clustersForThisModel, model.nodes]);

  // Build legend: only categories present in this model.
  const categoryLegend = useMemo<CanvasLegendEntry[]>(() => {
    const present = new Set(model.nodes.map((n) => n.node_type));
    return CATEGORY_LEGEND_ORDER
      .filter((type) => present.has(type))
      .map((type) => ({
        label: getNodeTypeLabel(type),
        color: getNodeTypeColor(type),
      }));
  }, [model.nodes]);

  // The ELK relayout is gated on STRUCTURE only (node/edge/cluster shape +
  // accent), never on positions — so persisting a drag doesn't snap clusters
  // back to the computed layout. The async effect reads the live model /
  // clusters / accents through refs instead of taking them as deps.
  const modelRef = useRef(model);
  const clustersForThisModelRef = useRef(clustersForThisModel);
  const accentByRegionIdRef = useRef(accentByRegionId);
  useEffect(() => { modelRef.current = model; }, [model]);
  useEffect(() => { clustersForThisModelRef.current = clustersForThisModel; }, [clustersForThisModel]);
  useEffect(() => { accentByRegionIdRef.current = accentByRegionId; }, [accentByRegionId]);

  const structureSignature = useMemo(() => JSON.stringify({
    model: model.model_id,
    nodes: model.nodes.map((n) => `${n.node_id}:${n.node_type}:${n.cluster_id ?? ''}`),
    conns: model.connections.map((c) => `${c.from_node_id}->${c.to_node_id}`),
    clusters: clustersForThisModel.map((c) => `${c.cluster_id}:${c.member_node_ids.join(',')}`),
    accents: Object.entries(accentByRegionId).map(([k, v]) => `${k}=${v}`).sort(),
  }), [model, clustersForThisModel, accentByRegionId]);

  // ELK layout
  useEffect(() => {
    let cancelled = false;

    async function doLayout() {
      const model = modelRef.current;
      const clustersForThisModel = clustersForThisModelRef.current;
      const accentByRegionId = accentByRegionIdRef.current;
      setLayoutDone(false);

      if (model.nodes.length === 0) {
        setNodes([]);
        setEdges([]);
        setGridColumns([]);
        setGridTiers([]);
        setGridWidth(0);
        setGridHeight(0);
        setLayoutDone(true);
        return;
      }

      const rawNodes = model.nodes.map((nd) => ({
        id: nd.node_id,
        type: 'modelingNode',
        width: MODELING_NODE_WIDTH,
        height: MODELING_NODE_HEIGHT,
        cluster_id: nd.cluster_id ?? null,
        data: {
          nodeType: nd.node_type,
          engineType: nd.engine,
          label: nd.name,
          runStatus: runStatusMapRef.current[nd.node_id],
          validationSummary: validationMapRef.current[nd.node_id],
          inputCount: nd.inputs?.length ?? 0,
          outputCount: nd.outputs?.length ?? 0,
          // INPUT_DATA "data available?" signal (owner #4): a data-requiring
          // input node is ready once a file is uploaded (active_upload_id set).
          inputDataReady:
            nd.node_type === 'input_data'
              ? Boolean((nd.metadata as any)?.active_upload_id)
              : undefined,
        } as ModelingNodeData,
        selected: nd.node_id === selectedNodeIdRef.current,
      }));

      const rawEdges = model.connections.map((conn) => ({
        id: conn.connection_id,
        source: conn.from_node_id,
        target: conn.to_node_id,
        type: 'modelingEdge',
        data: {
          artifactName: conn.artifact_name,
          sourceRunStatus: runStatusMapRef.current[conn.from_node_id],
        } as ModelingEdgeData,
      }));

      // Locked 2-D grid (replaces ELK): x = type column (canonical order, empty
      // types skipped), y = dependency tier (row 1 = no upstream dependency).
      // Neutral zebra lane bands + full-height column dividers; per-type node
      // colour. Nothing to drag, nothing to reset — the viewport is pinned.
      const { rfNodes, rfEdges, columns, tiers, gridWidth, gridHeight } =
        layoutProductionTiers(rawNodes, rawEdges);
      if (cancelled) return;

      setNodes(rfNodes);
      setEdges(rfEdges);
      setGridColumns(columns);
      setGridTiers(tiers);
      setGridWidth(gridWidth);
      setGridHeight(gridHeight);
      setLayoutDone(true);
    }

    doLayout();
    return () => { cancelled = true; };
    // Re-layout on structure / cluster membership / dominant-category changes
    // only — NOT on position saves. Live model/clusters/accent come via refs.
    // reLayoutVersion lets the "Reset layout" button force a fresh pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureSignature, reLayoutVersion, setEdges, setNodes]);

  // Re-apply saved cluster positions when they arrive/change after layout
  // (late-loading project state) without re-running ELK.
  useEffect(() => {
    setNodes((nds) => applySavedRegionPositions(nds, initialPositions));
  }, [initialPositions, setNodes]);

  // Update node data (run status, selection) without re-layout
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
        data: n.type === 'modelingNode'
          ? {
              ...n.data,
              runStatus: runStatusMap[n.id],
              validationSummary: validationMap[n.id],
            }
          : n.data,
      })),
    );
  }, [selectedNodeId, runStatusMap, validationMap, setNodes]);

  // Update edge data (run status) without re-layout
  useEffect(() => {
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        data: {
          ...e.data,
          sourceRunStatus: runStatusMap[e.source],
        },
      })),
    );
  }, [runStatusMap, setEdges]);

  // Pin the grid TOP-LEFT and fit to width — ONCE. Capture the zoom so the grid
  // never re-centers (even on AI updates), exactly like PlanGraphView.
  useEffect(() => {
    if (!isReactFlowReady || !layoutDone || hasPinned || gridWidth === 0) return;
    const inst = reactFlowInstance.current;
    const wrap = wrapperRef.current;
    if (!inst || !wrap) return;
    const t = setTimeout(() => {
      const availW = Math.max(240, wrap.clientWidth - GUTTER_W - 24);
      const zoom = Math.max(MIN_ZOOM, Math.min(1.1, availW / gridWidth));
      inst.setViewport({ x: GUTTER_W, y: HEADER_H, zoom }, { duration: 0 });
      setHasPinned(true);
    }, 0);
    return () => clearTimeout(t);
  }, [isReactFlowReady, layoutDone, hasPinned, gridWidth]);

  // Bounded movable workspace anchored top-left (mirrors PlanGraphView). The
  // negative insets (sized for MIN_ZOOM) let the grid origin sit just past the
  // frozen gutter+header at any zoom; lockTopLeft stops it drifting the other way.
  const translateExtent = useMemo<[[number, number], [number, number]]>(() => [
    [-GUTTER_W / MIN_ZOOM, -HEADER_H / MIN_ZOOM],
    [gridWidth + COLUMN_PAD, Math.max(gridHeight, 1)],
  ], [gridWidth, gridHeight]);

  // Hard-lock the top-left corner: never let the origin drift right of the gutter
  // or below the header, so the board stays pinned to the corner on pan AND zoom.
  const lockTopLeft = useCallback((vp: { x: number; y: number; zoom: number }) => {
    if (vp.x > GUTTER_W || vp.y > HEADER_H) {
      reactFlowInstance.current?.setViewport({ x: Math.min(vp.x, GUTTER_W), y: Math.min(vp.y, HEADER_H), zoom: vp.zoom });
    }
  }, []);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node.type === 'cluster-region') {
        setSelectedClusterId((prev) => (prev === node.id ? null : node.id));
        return;
      }
      if (node.type === 'tierLane' || node.type === 'columnBand') return;
      setSelectedClusterId(null);
      onNodeClick(node.id);
    },
    [onNodeClick],
  );

  // Empty-canvas click clears the cluster highlight AND the node selection
  // (onPaneClick from the parent, added in BAR-169) — clicking blank canvas
  // resets both. The only other escape from cluster-highlight mode is
  // re-clicking the same region.
  const handlePaneClick = useCallback(() => {
    setSelectedClusterId(null);
    onPaneClick();
  }, [onPaneClick]);

  // Notify the parent whenever cluster selection changes so it can drop node
  // selection (closes the NodeInfoBar) — the two are mutually exclusive. Single
  // notification point covers every source (toggle, switch to a node, pane click).
  useEffect(() => {
    onClusterSelect?.(selectedClusterId);
  }, [selectedClusterId, onClusterSelect]);

  const handleNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node.type === 'cluster-region' || node.type === 'tierLane' || node.type === 'columnBand') return;
      // INPUT_DATA nodes have no tool/engine — double-clicking them used to open
      // the empty "no computation engine" tool view. Select instead so the
      // inspector opens on its Upload Data tab (where the upload lives).
      const nodeType = (node.data as ModelingNodeData | undefined)?.nodeType;
      if (nodeType === 'input_data') { onNodeClick(node.id); return; }
      onNodeDoubleClick(node.id);
    },
    [onNodeClick, onNodeDoubleClick],
  );

  // Mirror current node state into a ref so the deferred save can read fresh
  // positions without abusing setNodes as a getter (React may batch or drop a
  // no-op setter callback).
  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  // Persist cluster-region positions when a drag ends. We save only the region
  // boxes (members + step numerals are parent-relative children that travel
  // with their box); deferred a tick so React Flow has applied the move to node
  // state (and the nodesRef effect has run) before we read positions.
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      const dragEnded = changes.some(
        (change) => change.type === 'position' && change.dragging === false,
      );
      if (!dragEnded || !editable || !onPositionsChange) return;
      setTimeout(() => {
        const positions: Record<string, { x: number; y: number }> = {};
        nodesRef.current.forEach((node) => {
          if (node.type === 'cluster-region') {
            positions[node.id] = { x: node.position.x, y: node.position.y };
          }
        });
        onPositionsChange(positions);
      }, 0);
    },
    [onNodesChange, editable, onPositionsChange],
  );

  // "Reset layout": discard any saved cluster-region positions and re-run the
  // ELK auto-layout from scratch. Clear the ref synchronously so the forced
  // re-layout (below) doesn't re-apply the old override, then persist the clear
  // so it survives reload, then bump the version to trigger a fresh ELK pass.
  const handleResetLayout = useCallback(() => {
    initialPositionsRef.current = {};
    onPositionsChange?.({});
    setReLayoutVersion((version) => version + 1);
  }, [onPositionsChange]);

  // Normalised edge list for cluster highlighting (key = connection_id, matching
  // the rendered modeling edge ids).
  const highlightEdges = useMemo<HighlightEdge[]>(
    () => model.connections.map((conn) => ({
      source: conn.from_node_id,
      target: conn.to_node_id,
      key: conn.connection_id,
    })),
    [model.connections],
  );

  // Member node ids for a cluster region. The unsorted pseudo-cluster has no
  // Cluster record, so its members are nodes with no (known) cluster.
  const clusterMemberIds = useCallback((clusterId: string): string[] => {
    if (clusterId === UNSORTED_CLUSTER_NODE_ID) {
      const known = new Set(clustersForThisModel.map((c) => c.cluster_id));
      return model.nodes
        .filter((n) => !n.cluster_id || !known.has(n.cluster_id))
        .map((n) => n.node_id);
    }
    return clustersForThisModel.find((c) => c.cluster_id === clusterId)?.member_node_ids ?? [];
  }, [clustersForThisModel, model.nodes]);

  // Compute highlight state — cluster members + their connections when a cluster
  // is selected, otherwise the selected node's direct neighbours.
  const highlightState = useMemo<HighlightState>(() => {
    // A hovered/selected node wins (hover first, like the plan graph's
    // activeId = hoveredId || selectedId); only fall back to cluster highlight
    // when no node is active. Lighting a node's incident edges is also what
    // surfaces their labels (ModelingEdge shows the artifact name on-path).
    const activeNodeId = hoveredNodeId || selectedNodeId;
    if (activeNodeId) {
      const nodeIds = new Set<string>([activeNodeId]);
      const edgeKeys = new Set<string>();
      model.connections.forEach((conn) => {
        if (conn.to_node_id === activeNodeId) {
          nodeIds.add(conn.from_node_id);
          edgeKeys.add(conn.connection_id);
        }
        if (conn.from_node_id === activeNodeId) {
          nodeIds.add(conn.to_node_id);
          edgeKeys.add(conn.connection_id);
        }
      });
      if (nodeIds.size <= 1) return EMPTY_HIGHLIGHT; // No connected edges
      return { nodeIds, edgeKeys, active: true };
    }
    if (selectedClusterId) {
      return clusterHighlight(clusterMemberIds(selectedClusterId), highlightEdges);
    }
    return EMPTY_HIGHLIGHT;
  }, [hoveredNodeId, selectedNodeId, selectedClusterId, clusterMemberIds, highlightEdges, model]);

  // Edge-label overlay data (owner #3): the active (hovered/selected) node's
  // incident-edge artifact names, positioned at each edge's midpoint. Rendered
  // by ProductionEdgeLabels inside ReactFlow — replaces the per-edge label that
  // didn't surface in the locked grid. Mirrors the plan graph's hover/select.
  const litLabels = useMemo(() => {
    const activeNodeId = hoveredNodeId || selectedNodeId;
    if (!activeNodeId || !model) return [];
    const pos = new Map<string, { x: number; y: number }>();
    nodes.forEach((n) => {
      if (n.type === 'modelingNode' && n.position) pos.set(n.id, n.position);
    });
    const out: { id: string; x: number; y: number; text: string }[] = [];
    model.connections.forEach((conn) => {
      if (conn.from_node_id !== activeNodeId && conn.to_node_id !== activeNodeId) return;
      const name = decodeEntities(conn.artifact_name || '');
      if (!name) return;
      const a = pos.get(conn.from_node_id);
      const b = pos.get(conn.to_node_id);
      if (!a || !b) return;
      const ax = a.x + MODELING_NODE_WIDTH / 2;
      const ay = a.y + MODELING_NODE_HEIGHT / 2;
      const bx = b.x + MODELING_NODE_WIDTH / 2;
      const by = b.y + MODELING_NODE_HEIGHT / 2;
      out.push({
        id: conn.connection_id,
        x: (ax + bx) / 2,
        y: (ay + by) / 2,
        text: name.length > 26 ? `${name.slice(0, 26)}…` : name,
      });
    });
    return out;
  }, [hoveredNodeId, selectedNodeId, model, nodes]);

  // When a node/cluster is selected, lift its connected edges ABOVE the cards
  // and boxes so the connections read crystal-clear on top (they're otherwise
  // occluded at the resting layer); everything else returns to resting. Mirrors
  // the same edgeKeys set ModelingEdge uses to colour the highlighted path.
  // Functional + identity-preserving so it doesn't churn unrelated edge state.
  useEffect(() => {
    setEdges((eds) =>
      eds.map((e) => {
        const z = highlightState.active && highlightState.edgeKeys.has(e.id)
          ? GRAPH_LAYER.edgeElevated
          : GRAPH_LAYER.edgeResting;
        return e.zIndex === z ? e : { ...e, zIndex: z };
      }),
    );
  }, [highlightState, setEdges]);

  // Live run-progress band (only while a run is in flight). Rendered between the
  // canvas header and the grid via GraphCanvas's progressBar slot, so it never
  // overlaps the frozen column header inside the canvas.
  const progressBar = runProgress ? (
    <div className="mb-3 flex flex-shrink-0 items-center gap-3 rounded-md border border-divider bg-bg-warm/50 px-3 py-2">
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-warning animate-pulse" />
      <span className="flex-shrink-0 text-[12px] font-semibold text-fg">
        {runProgress.failed ? 'Run failed' : 'Running'} · {runProgress.done}/{runProgress.total}
      </span>
      {runProgress.runningName && !runProgress.failed && (
        <span className="min-w-0 truncate text-[12px] text-fg-secondary">{runProgress.runningName}</span>
      )}
      <div className="ml-auto h-1.5 w-40 flex-shrink-0 overflow-hidden rounded-full bg-bg-warm">
        <div
          className={`h-full rounded-full transition-all duration-300 ${runProgress.failed ? 'bg-danger' : 'bg-accent'}`}
          style={{ width: `${Math.round((runProgress.done / Math.max(1, runProgress.total)) * 100)}%` }}
        />
      </div>
    </div>
  ) : undefined;

  if (!layoutDone) {
    return (
      <GraphCanvas
        legend={categoryLegend}
        headerActions={headerActions}
        progressBar={progressBar}
        showEdgeLabels={showEdgeLabels}
        onToggleEdgeLabels={setShowEdgeLabels}
        hideEdgeLabelToggle
      >
        <div className="h-full flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </GraphCanvas>
    );
  }

  return (
    <GraphCanvas
      legend={categoryLegend}
      headerActions={headerActions}
      progressBar={progressBar}
      showEdgeLabels={showEdgeLabels}
      onToggleEdgeLabels={setShowEdgeLabels}
      hideEdgeLabelToggle
    >
      <HighlightContext.Provider value={highlightState}>
      <SelectedClusterContext.Provider value={selectedClusterId}>
      <div ref={wrapperRef} className="h-full w-full relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onNodeMouseEnter={(_event, node) => { if (node.type === 'modelingNode') setHoveredNodeId(node.id); }}
          onNodeMouseLeave={() => setHoveredNodeId(null)}
          onPaneClick={handlePaneClick}
          onInit={(instance) => { reactFlowInstance.current = instance; setIsReactFlowReady(true); }}
          onMove={(_event, vp) => lockTopLeft(vp)}
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
          translateExtent={translateExtent}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          panOnScroll
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
        >
          {gridColumns.length > 0 && <TypeColumnHeader columns={gridColumns} />}
          {gridTiers.length > 0 && <TierGutter tiers={gridTiers} />}
          <CornerBox />
          <ProductionEdgeLabels labels={litLabels} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeColor={(node) => {
              if (node.type === 'tierLane' || node.type === 'columnBand') return 'transparent';
              if (node.type === 'cluster-region') {
                return resolveCssVar('var(--border-strong)');
              }
              const nd = node.data as ModelingNodeData;
              return getNodeTypeColorResolved(nd.nodeType);
            }}
            maskColor="rgb(248, 250, 252, 0.7)"
            style={{ height: 80, width: 120 }}
          />
        </ReactFlow>
      </div>
      </SelectedClusterContext.Provider>
      </HighlightContext.Provider>
    </GraphCanvas>
  );
}
